import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";

const sendOne = async (f, onBytes) => {
  const fd = new FormData();
  fd.append("files", f);
  for (let attempt = 0; ; attempt += 1) {
    try {
      const { data } = await api.post("/admin/upload", fd, { onUploadProgress: (e) => onBytes(e.loaded) });
      return data.urls[0];
    } catch (err) {
      onBytes(0);
      const s = err?.response?.status;
      if (attempt >= 2 || (s && s < 500 && s !== 429)) throw err;
      await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
    }
  }
};

export const uploadFiles = async (files, onProgress) => {
  const urls = [];
  const failed = [];
  const total = files.reduce((s, f) => s + f.size, 0) || 1;
  let done = 0;
  for (let i = 0; i < files.length; i += 1) {
    const report = (loaded) => onProgress?.({ index: i + 1, count: files.length, pct: Math.min(99, Math.round(((done + Math.min(loaded, files[i].size)) / total) * 100)) });
    report(0);
    try { urls.push(await sendOne(files[i], report)); } catch (err) { failed.push(`${files[i].name}: ${formatErr(err)}`); }
    done += files[i].size;
    report(0);
  }
  onProgress?.({ index: files.length, count: files.length, pct: 100 });
  return { urls, failed };
};

const Progress = ({ p }) => (
  <span className="flex flex-col gap-1.5 min-w-[220px]" data-testid="upload-progress">
    <span className="flex justify-between text-xs text-slate-600"><span>Foto {p.index} di {p.count}</span><b data-testid="upload-progress-pct">{p.pct}%</b></span>
    <span className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden"><span className="block h-full bg-slate-900 transition-[width] duration-300" style={{ width: `${p.pct}%` }} /></span>
  </span>
);

export const ImageUploader = ({ onUploaded, multiple = true, label = "Carica foto", testid = "image-uploader", accept = "image/jpeg,image/png,image/webp" }) => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(null);
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setProgress({ index: 1, count: files.length, pct: 0 });
    const { urls, failed } = await uploadFiles(files, setProgress);
    if (urls.length) { onUploaded(urls); toast.success(`${urls.length} foto caricate`); }
    if (failed.length) toast.error(`${failed.length} foto non caricate. Riprovate con quelle mancanti.`, { description: failed.slice(0, 3).join("\n"), duration: 10000 });
    setProgress(null);
  };
  const busy = !!progress;
  return (
    <span className="inline-flex flex-wrap items-center gap-4">
      <input ref={ref} type="file" accept={accept} multiple={multiple} hidden onChange={onPick} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={testid}
        className="inline-flex items-center gap-2 border border-dashed border-mute/60 px-4 py-2.5 text-sm rounded-sm hover:border-ink hover:bg-white transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {busy ? "Caricamento in corso..." : label}
      </button>
      {busy && <Progress p={progress} />}
    </span>
  );
};
